import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  House, Buildings, CreditCard, Users, Receipt, ChartLine, Stack,
  Coins, ListChecks, Broadcast, Gear, SignOut, BookOpen, Wallet,
  FileArrowUp, Plug,
} from "@phosphor-icons/react";
import { auth } from "@/lib/api";
import { BlonixWordmark } from "@/components/BlonixLogo";

const NAV = [
  { to: "/", label: "Overview", icon: House, end: true },
  { to: "/merchants", label: "Merchants", icon: Buildings },
  { to: "/balance-funding", label: "Balance funding", icon: Wallet },
  { to: "/kyb-queue", label: "KYB Queue", icon: FileArrowUp },
  { to: "/cards", label: "Cards", icon: CreditCard },
  { to: "/users", label: "Cardholders", icon: Users },
  { to: "/transactions", label: "Transactions", icon: Receipt },
  { to: "/analytics", label: "Analytics", icon: ChartLine },
];

const NAV2 = [
  { to: "/card-types", label: "Card Types", icon: Stack },
  { to: "/fees", label: "Fees", icon: Coins },
  { to: "/providers", label: "Providers", icon: Plug },
  { to: "/audit-logs", label: "Audit Logs", icon: ListChecks },
  { to: "/webhooks", label: "Webhooks", icon: Broadcast },
  { to: "/api-docs", label: "API Reference", icon: BookOpen },
  { to: "/settings", label: "Settings", icon: Gear },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const user = auth.user();

  const onLogout = async () => {
    await auth.logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-zinc-100" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-zinc-950 text-zinc-300 flex flex-col fixed inset-y-0 left-0 z-30" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-zinc-900">
          <BlonixWordmark className="h-5 w-auto" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">Card Platform</div>
        </div>

        <nav className="flex-1 overflow-y-auto sb-scroll px-3 py-4 space-y-6">
          <div>
            <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-zinc-600">Operations</div>
            <ul className="space-y-0.5">
              {NAV.map((it) => <SideLink key={it.to} {...it} />)}
            </ul>
          </div>
          <div>
            <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-zinc-600">Configuration</div>
            <ul className="space-y-0.5">
              {NAV2.map((it) => <SideLink key={it.to} {...it} />)}
            </ul>
          </div>
        </nav>

        <div className="border-t border-zinc-900 p-3">
          <div className="px-3 py-2 flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-zinc-800 flex items-center justify-center text-xs font-semibold text-white">
              {(user?.name || "A").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-white truncate" data-testid="sidebar-user-name">{user?.name || "Admin"}</div>
              <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={onLogout}
              data-testid="sidebar-logout-btn"
              className="text-zinc-400 hover:text-white transition-colors duration-150"
              title="Sign out"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 ml-64">
        <div className="page-enter">{children}</div>
      </main>
    </div>
  );
}

function SideLink({ to, label, icon: Icon, end }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          [
            "group flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors duration-150",
            isActive
              ? "bg-zinc-900 text-white border-l-2 border-blue-500 pl-[10px]"
              : "text-zinc-400 hover:text-white hover:bg-zinc-900",
          ].join(" ")
        }
      >
        <Icon size={16} weight="regular" />
        <span>{label}</span>
      </NavLink>
    </li>
  );
}

export function PageHeader({ title, subtitle, actions, testId }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8" data-testid={testId || "page-header"}>
      <div>
        <h1 className="font-display text-3xl tracking-tight font-medium text-zinc-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Surface({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`bg-white border border-zinc-200 rounded-sm ${className}`}
    >
      {children}
    </div>
  );
}

export function StatusPill({ value }) {
  const map = {
    active: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    approved: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    completed: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    delivered: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    pending: "bg-amber-50 text-amber-700 border border-amber-100",
    not_submitted: "bg-zinc-100 text-zinc-600 border border-zinc-200",
    submitted: "bg-blue-50 text-blue-700 border border-blue-100",
    frozen: "bg-amber-50 text-amber-700 border border-amber-100",
    suspended: "bg-red-50 text-red-700 border border-red-100",
    rejected: "bg-red-50 text-red-700 border border-red-100",
    failed: "bg-red-50 text-red-700 border border-red-100",
    terminated: "bg-zinc-100 text-zinc-600 border border-zinc-200",
  };
  const cls = map[(value || "").toLowerCase()] || "bg-zinc-100 text-zinc-700 border border-zinc-200";
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.05em] px-2 py-0.5 rounded-sm ${cls}`}>
      {value || "—"}
    </span>
  );
}
